export const ACTIVITY_TYPES = {
  call: "call",
  note: "note",
  offer: "offer",
  file: "file",
  task: "task",
  taskOpen: "task_open",
  taskCompleted: "task_completed",
  sms: "sms",
  email: "email",
  meeting: "meeting",
  feedback: "feedback",
}

export const TYPE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: ACTIVITY_TYPES.call, label: "Calls" },
  { value: ACTIVITY_TYPES.note, label: "Notes" },
  { value: ACTIVITY_TYPES.email, label: "Emails" },
  { value: ACTIVITY_TYPES.sms, label: "SMS" },
  { value: ACTIVITY_TYPES.offer, label: "Proposals" },
  { value: ACTIVITY_TYPES.task, label: "Tasks" },
  { value: ACTIVITY_TYPES.meeting, label: "Meetings" },
  { value: ACTIVITY_TYPES.feedback, label: "Feedback" },
]

/** Ordered insight cards for the activity dashboard. */
export const INSIGHT_CARD_CONFIG = [
  {
    key: ACTIVITY_TYPES.call,
    label: "Calls",
    accent: "#0F766E",
    soft: "rgba(15, 118, 110, 0.12)",
  },
  {
    key: ACTIVITY_TYPES.sms,
    label: "SMS",
    accent: "#0369A1",
    soft: "rgba(3, 105, 161, 0.12)",
  },
  {
    key: ACTIVITY_TYPES.email,
    label: "Emails",
    accent: "#C2410C",
    soft: "rgba(194, 65, 12, 0.12)",
  },
  {
    key: ACTIVITY_TYPES.note,
    label: "Notes",
    accent: "#4B5563",
    soft: "rgba(75, 85, 99, 0.12)",
  },
  {
    key: ACTIVITY_TYPES.meeting,
    label: "Meetings",
    accent: "#7C3AED",
    soft: "rgba(124, 58, 237, 0.12)",
  },
  {
    key: ACTIVITY_TYPES.task,
    label: "Tasks",
    accent: "#B45309",
    soft: "rgba(180, 83, 9, 0.12)",
    combinesTaskStatus: true,
  },
  {
    key: ACTIVITY_TYPES.offer,
    label: "Proposals",
    accent: "#BE185D",
    soft: "rgba(190, 24, 93, 0.12)",
  },
  {
    key: ACTIVITY_TYPES.file,
    label: "Files",
    accent: "#475569",
    soft: "rgba(71, 85, 105, 0.12)",
  },
  {
    key: ACTIVITY_TYPES.feedback,
    label: "Feedback",
    accent: "#0D9488",
    soft: "rgba(13, 148, 136, 0.12)",
  },
]

export const TYPE_COUNT_LABELS = INSIGHT_CARD_CONFIG.map(({ key, label }) => ({
  key,
  label,
}))

function isCompletedTaskActivity(activity) {
  return (
    activity?.activity_type === ACTIVITY_TYPES.taskCompleted ||
    activity?.title === "Task completed"
  )
}

function isOpenTaskActivity(activity) {
  if (isCompletedTaskActivity(activity)) {
    return false
  }
  return (
    activity?.activity_type === ACTIVITY_TYPES.taskOpen ||
    activity?.activity_type === ACTIVITY_TYPES.task ||
    activity?.title === "Task added" ||
    activity?.title === "Task marked not complete"
  )
}

/**
 * Resolve open/completed task counts from API type_counts, with a fallback
 * from loaded activity rows when the API only returns a legacy `task` total.
 */
export function resolveTaskInsightCounts(typeCounts = {}, activities = []) {
  let openCount = Number(typeCounts[ACTIVITY_TYPES.taskOpen] || 0)
  let completedCount = Number(typeCounts[ACTIVITY_TYPES.taskCompleted] || 0)
  const legacyTaskCount = Number(typeCounts[ACTIVITY_TYPES.task] || 0)

  if (openCount + completedCount > 0) {
    return {
      openCount,
      completedCount,
      count: openCount + completedCount,
    }
  }

  if (legacyTaskCount > 0 && activities.length > 0) {
    openCount = 0
    completedCount = 0
    activities.forEach((activity) => {
      if (isCompletedTaskActivity(activity)) {
        completedCount += 1
      } else if (isOpenTaskActivity(activity)) {
        openCount += 1
      }
    })
    const derived = openCount + completedCount
    return {
      openCount,
      completedCount,
      count: derived > 0 ? derived : legacyTaskCount,
    }
  }

  return {
    openCount: 0,
    completedCount: 0,
    count: legacyTaskCount,
  }
}

export function buildTypeCountRows(typeCounts = {}) {
  const rows = TYPE_COUNT_LABELS.map(({ key, label }) => {
    if (key === ACTIVITY_TYPES.task) {
      const { count } = resolveTaskInsightCounts(typeCounts)
      return { key, label, count }
    }
    return {
      key,
      label,
      count: Number(typeCounts[key] || 0),
    }
  }).filter((row) => row.count > 0)

  Object.entries(typeCounts).forEach(([key, count]) => {
    if (
      TYPE_COUNT_LABELS.some((row) => row.key === key) ||
      key === ACTIVITY_TYPES.taskOpen ||
      key === ACTIVITY_TYPES.taskCompleted
    ) {
      return
    }
    const value = Number(count || 0)
    if (value > 0) {
      rows.push({
        key,
        label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        count: value,
      })
    }
  })

  return rows
}

export function buildInsightCards(
  typeCounts = {},
  totalActivities = 0,
  activities = []
) {
  const cards = INSIGHT_CARD_CONFIG.map((config) => {
    if (config.combinesTaskStatus) {
      const { openCount, completedCount, count } = resolveTaskInsightCounts(
        typeCounts,
        activities
      )
      return {
        ...config,
        count,
        openCount,
        completedCount,
      }
    }
    return {
      ...config,
      count: Number(typeCounts[config.key] || 0),
    }
  })
  const knownTotal = cards.reduce((sum, card) => sum + card.count, 0)
  const resolvedTotal = Number(totalActivities || knownTotal)
  return {
    total: resolvedTotal,
    cards,
  }
}

/** Highlight Tasks card when filtering by task, task_open, or task_completed. */
export function isTaskInsightSelected(selectedType) {
  return (
    selectedType === ACTIVITY_TYPES.task ||
    selectedType === ACTIVITY_TYPES.taskOpen ||
    selectedType === ACTIVITY_TYPES.taskCompleted
  )
}

export function getTypeFilterCount(typeValue, summary) {
  if (!typeValue) {
    return summary?.total ?? 0
  }
  const card = summary?.cards?.find((item) => item.key === typeValue)
  return card?.count ?? 0
}

export const ACTIVITY_TABS = {
  activity: "activity",
  powerDialing: "power_dialing",
  // Reports used to be its own sidebar page; /reports redirects here.
  reports: "reports",
}

export function resolveActivityTab(tabFromUrl, showPowerDialing) {
  if (tabFromUrl === ACTIVITY_TABS.reports) {
    return ACTIVITY_TABS.reports
  }
  if (showPowerDialing && tabFromUrl === ACTIVITY_TABS.powerDialing) {
    return ACTIVITY_TABS.powerDialing
  }
  return ACTIVITY_TABS.activity
}
